import React from 'react';
import { useDashboard } from '../contexts/DashboardContext';
import EditableText from '../components/EditableText';
import { Briefcase, DollarSign, TrendingUp } from 'lucide-react';

const PortfolioSection = () => {
  const { config, updateField } = useDashboard();

  if (!config) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-4xl font-bold text-slate-900 mb-2">Portfolio Overview</h2>
        <p className="text-slate-600">Comprehensive view of your trading portfolio</p>
      </div>

      {/* Portfolio Info */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <label className="kpi-label block mb-2">
              Portfolio Name
            </label>
            <div className="text-2xl font-bold text-slate-900">
              <EditableText
                value={config.portfolio_name}
                field="portfolio_name"
                onUpdate={updateField}
                type="text"
              />
            </div>
          </div>
          <div>
            <label className="kpi-label block mb-2">
              Investor Name
            </label>
            <div className="text-2xl font-bold text-slate-900">
              <EditableText
                value={config.investor_name}
                field="investor_name"
                onUpdate={updateField}
                type="text"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-emerald-50">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Total Value</p>
              <p className="text-3xl font-bold text-slate-900 financial-number">
                ${config.total_portfolio_value.toLocaleString()}
              </p>
            </div>
          </div>
          <p className="text-sm text-slate-600">Current portfolio valuation</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-blue-50">
              <Briefcase className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Holdings</p>
              <p className="text-3xl font-bold text-slate-900 financial-number">{config.total_holdings}</p>
            </div>
          </div>
          <p className="text-sm text-slate-600">Active positions</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-purple-50">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Win Rate</p>
              <p className="text-3xl font-bold text-slate-900 financial-number">{config.win_rate}%</p>
            </div>
          </div>
          <p className="text-sm text-slate-600">Success rate</p>
        </div>
      </div>

      {/* Open Positions Table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Open Positions</h3>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="open-positions-table">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-3 text-sm font-medium text-slate-600">Asset</th>
                <th className="text-right py-3 px-3 text-sm font-medium text-slate-600">Entry</th>
                <th className="text-right py-3 px-3 text-sm font-medium text-slate-600">Current</th>
                <th className="text-right py-3 px-3 text-sm font-medium text-slate-600">Stop</th>
                <th className="text-right py-3 px-3 text-sm font-medium text-slate-600">Unrealized P&L</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="py-3 px-3 text-sm font-medium text-slate-900">BTC</td>
                <td className="text-right py-3 px-3 table-number text-slate-900">$65,234</td>
                <td className="text-right py-3 px-3 table-number text-slate-900">$67,234</td>
                <td className="text-right py-3 px-3 table-number text-slate-900">$63,000</td>
                <td className="text-right py-3 px-3 table-number font-medium positive-value">+$2,000</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-3 px-3 text-sm font-medium text-slate-900">XAU/USD</td>
                <td className="text-right py-3 px-3 table-number text-slate-900">$2,050</td>
                <td className="text-right py-3 px-3 table-number text-slate-900">$2,043</td>
                <td className="text-right py-3 px-3 table-number text-slate-900">$2,000</td>
                <td className="text-right py-3 px-3 table-number font-medium negative-value">-$7</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-3 px-3 text-sm font-medium text-slate-900">SPX</td>
                <td className="text-right py-3 px-3 table-number text-slate-900">$5,200</td>
                <td className="text-right py-3 px-3 table-number text-slate-900">$5,234</td>
                <td className="text-right py-3 px-3 table-number text-slate-900">$5,150</td>
                <td className="text-right py-3 px-3 table-number font-medium positive-value">+$34</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PortfolioSection;